import React, { useEffect } from 'react';
import { Stack, Text, Group, Avatar, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useOptimisticShare } from '../hooks/use-optimistic-share';
import { sharedApi } from '../api/shared';
import closeIcon from '../assets/icons/close.svg';

interface SharedMember {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'active' | 'declined';
  joined_at?: string;
}

interface SharedMembersListProps {
  type: 'account' | 'budget';
  resourceId: string;
  members: SharedMember[];
  isOwner: boolean;
  onRemoveMember?: (memberId: string) => Promise<void>;
}

export const SharedMembersList = ({
  type,
  resourceId,
  members,
  isOwner,
  onRemoveMember,
}: SharedMembersListProps) => {
  const {
    optimisticMembers,
    clearPendingUpdates,
    hasPendingUpdates
  } = useOptimisticShare(type, resourceId);

  // Clear pending updates when unmounting
  useEffect(() => {
    return () => {
      if (hasPendingUpdates) {
        clearPendingUpdates();
      }
    };
  }, [hasPendingUpdates, clearPendingUpdates]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'declined':
        return 'red';
      default:
        return 'gray';
    }
  };

  const handleRemoveMember = async (member: SharedMember) => {
    if (!onRemoveMember) return;
    
    if (!confirm(`Remove ${member.email} from this ${type}? This action cannot be undone.`)) {
      return;
    }

    try {
      // Check if user has permission to remove member
      const hasPermission = await sharedApi.checkPermission(type, resourceId, 'manage_members');
      if (!hasPermission) {
        notifications.show({
          title: 'Permission Denied',
          message: 'You do not have permission to remove members',
          color: 'red',
        });
        return;
      }

      await onRemoveMember(member.id);
      notifications.show({
        title: 'Member Removed',
        message: `Removed ${member.email} from ${type}`,
        color: 'blue',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove member';
      if (message.includes('pending transactions')) {
        notifications.show({
          title: 'Cannot Remove Member',
          message: 'This member has pending transactions that must be resolved first',
          color: 'yellow',
        });
      } else {
        notifications.show({
          title: 'Error',
          message,
          color: 'red',
        });
      }
    }
  };

  // Combine real and optimistic members
  const allMembers = [...members];
  optimisticMembers.forEach((opt: SharedMember) => {
    if (!allMembers.some(m => m.email === opt.email)) {
      allMembers.push(opt);
    }
  });

  return (
    <Stack>
      <Text size="sm" fw={500}>Shared With</Text>
      {allMembers.map((member) => (
        <Group 
          key={member.id} 
          justify="space-between"
          p="xs" 
          bg="gray.0" 
          style={{ 
            borderRadius: 8,
            opacity: member.status === 'pending' ? 0.7 : 1
          }}
        >
          <Group gap="md">
            <Avatar 
              size="sm" 
              radius="xl" 
              color={member.status === 'active' ? 'blue' : 'gray'}
            >
              {(member.name?.[0] || member.email[0]).toUpperCase()}
            </Avatar>
            <Stack gap="xs">
              <Text size="sm">
                {member.name || member.email}
              </Text>
              <Group gap="xs">
                <Badge 
                  size="sm" 
                  color={getStatusColor(member.status)}
                >
                  {member.status}
                </Badge>
                {member.status === 'pending' && (
                  <Text size="xs" c="dimmed">
                    Invited {new Date(member.joined_at || Date.now()).toLocaleDateString()}
                  </Text>
                )}
                {member.joined_at && member.status === 'active' && (
                  <Text size="xs" c="dimmed">
                    Joined {new Date(member.joined_at).toLocaleDateString()}
                  </Text>
                )}
              </Group>
            </Stack>
          </Group>

          {isOwner && member.status === 'active' && !member.id.startsWith('temp-') && (
            <Tooltip label={`Remove from ${type}`}>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => handleRemoveMember(member)}
              >
                <img src={closeIcon} alt="Remove" width={16} height={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      ))}
      {allMembers.length === 0 && (
        <Text c="dimmed" ta="center" py="sm">
          No shared members yet
        </Text>
      )}
    </Stack>
  );
}; 